import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";

/**
 * Ein String-Wert im Query-String (?key=value). Der Standardwert steht nicht
 * in der URL, damit Links kurz bleiben. Ansichten (Tab, Suche, Seite) bleiben
 * so beim Zurückgehen erhalten und lassen sich als Link teilen.
 */
export function useUrlParam(
  key: string,
  defaultValue: string,
): [string, (value: string, opts?: { resetPage?: boolean }) => void] {
  const [params, setParams] = useSearchParams();
  const value = params.get(key) ?? defaultValue;

  const setValue = useCallback(
    (next: string, opts?: { resetPage?: boolean }) => {
      setParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          if (next === defaultValue || next === "") p.delete(key);
          else p.set(key, next);
          if (opts?.resetPage) p.delete("page");
          return p;
        },
        { replace: true },
      );
    },
    [key, defaultValue, setParams],
  );

  return [value, setValue];
}

/** Wie useUrlParam, aber als Zahl (z. B. Seite, Zeitraum in Tagen). */
export function useUrlNumber(
  key: string,
  defaultValue: number,
): [number, (value: number) => void] {
  const [raw, setRaw] = useUrlParam(key, String(defaultValue));
  const parsed = Number(raw);
  const value = Number.isFinite(parsed) ? parsed : defaultValue;
  const setValue = useCallback((n: number) => setRaw(String(n)), [setRaw]);
  return [value, setValue];
}
